import { NavLink, useMatch } from 'react-router-dom';
import { useLibrarySections } from '@/lib/queries.ts';
import { navProps } from '@/ui/Focusable.tsx';

/**
 * The way between libraries — a strip of words under the main navigation.
 *
 * It used to be a row of poster-shaped tiles at the BOTTOM of the library
 * home, on the reasoning that browsing is what you do when the curated rows
 * did not have it. That reasoning was about the home screen and ignored the
 * other half of the job: switching from Movies to TV Shows meant backing out
 * of the grid you were in, scrolling past two rows, and picking from tiles
 * the size of the film posters beside them — a lot of screen and a lot of
 * presses for what is really just a tab. Now it is words, it is first, and it
 * is there on the section pages too, which is where the want actually arises.
 *
 * RENDERED ONCE BY THE SHELL, not by each page, and this is not tidiness.
 *
 * A per-page copy unmounts and remounts when you use it, which destroys the
 * element holding focus at the instant of the press. Focus falls to <body>,
 * and the next arrow key teleports to wherever the grid happens to start —
 * so the control would break every time it worked. Living beside <nav>, it
 * survives the navigation and the chip you pressed stays focused, which also
 * means you can keep walking sideways through the libraries.
 *
 * IT SITS OUTSIDE `.main`, WHICH IS WHAT MAKES IT BEHAVE.
 *
 * That gets the good half of `position: sticky` — it stays on screen while
 * you scroll a four-hundred-film grid — without the bad half. Spatial
 * navigation buckets candidates by scroll surface (ui/spatial.ts), so a
 * sticky bar INSIDE `.main` would share a surface with the grid while pinned
 * to the top of the viewport, and pressing UP from row five would land on it
 * rather than on row four: nearer in viewport coordinates, same bucket. Out
 * here it is a different surface, so it always loses to a real row above and
 * wins only when there is none. That is precisely the rule wanted, and it
 * comes for free from a decision already made.
 *
 * Focus does NOT start here — the first poster still takes it, because
 * resuming something is four visits out of five. This is what UP is for, and
 * UP was otherwise doing nothing on this screen.
 */
export function LibraryBar() {
  const { data: sections } = useLibrarySections();

  /*
   * The library routes and no others. Item pages are deliberately excluded:
   * a detail page is a full-bleed backdrop with a Play button on it, and a
   * row of tabs across the top of that is chrome interrupting the one screen
   * in the app that is trying to look like something.
   */
  const atHome = useMatch('/library');
  const inSection = useMatch('/library/s/:key');
  if (!atHome && !inSection) return null;
  if (!(sections ?? []).length) return null;

  return (
    <nav className="libe-bar" data-nav-zone="top" aria-label="Libraries">
      {/*
        `end` matters: without it this stays marked current on every
        /library/* route, so walking into Movies would leave two chips lit and
        the bar would stop telling you where you are — which is most of what
        it is for.
      */}
      <NavLink end to="/library" {...navProps('libbar-home')}>Home</NavLink>
      {sections!.map((s) => (
        <NavLink
          key={s.key}
          to={`/library/s/${encodeURIComponent(s.key)}`}
          {...navProps(`libbar-${s.key}`)}
        >
          {s.title}
        </NavLink>
      ))}
    </nav>
  );
}
